import { useEffect, useMemo, useRef } from 'react';
import useMarketIntelligenceStore from '../Stores/marketIntelligenceStore';


const DEFAULT_LOCAL_WS_URL = 'ws://localhost:8765/ws';


const WS_URL =
  process.env.REACT_APP_MARKET_INTELLIGENCE_WS_URL || DEFAULT_LOCAL_WS_URL;

const AUTH_MODE = process.env.REACT_APP_AUTH_MODE || 'manual';

const RECONNECT_DELAY_MS = 3000;
const CLIENT_PING_INTERVAL_MS = 30000;

const LOCAL_AUTH = {
  memberId: process.env.REACT_APP_LOCAL_MEMBER_ID,
  planId: process.env.REACT_APP_LOCAL_PLAN_ID,
  status: process.env.REACT_APP_LOCAL_PLAN_STATUS || 'active',
};

function normalizeStatus(value) {
  return String(value || '').trim();
}

function normalizeMessage(rawMessage) {
  try {
    return JSON.parse(rawMessage);
  } catch (error) {
    console.warn('[MIS WSS] Failed to parse message:', rawMessage);
    return null;
  }
}

function getMessageType(message) {
  return message?.type || message?.message_type || message?.event || null;
}

function getMessagePayload(message) {
  return message?.payload || message?.data || message || {};
}

function hasValidCredentials(credentials) {
  return Boolean(
    credentials?.memberId &&
      credentials?.planId &&
      credentials?.status
  );
}

function getEffectiveCredentials(memberCredentials) {
  if (hasValidCredentials(memberCredentials)) {
    return {
      memberId: memberCredentials.memberId,
      planId: memberCredentials.planId,
      status: normalizeStatus(memberCredentials.status),
    };
  }

  if (AUTH_MODE === 'manual' && hasValidCredentials(LOCAL_AUTH)) {
    return {
      memberId: LOCAL_AUTH.memberId,
      planId: LOCAL_AUTH.planId,
      status: normalizeStatus(LOCAL_AUTH.status),
    };
  }

  return null;
}

function buildAuthPayload(credentials) {
  return {
    type: 'authenticate',
    data: {
      memberId: credentials.memberId,
      status: credentials.status,
      planId: credentials.planId,
    },
  };
}

function MarketIntelligenceWebsocketClient({ memberCredentials }) {
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const stopRequestedRef = useRef(false);

  const effectiveCredentials = useMemo(
    () => getEffectiveCredentials(memberCredentials),
    [
      memberCredentials?.memberId,
      memberCredentials?.planId,
      memberCredentials?.status,
    ]
  );

  useEffect(() => {
    // console.log('[MIS WSS] component mounted/rendered');
    // console.log('[MIS WSS] AUTH_MODE:', AUTH_MODE);
    // console.log('[MIS WSS] WS_URL:', WS_URL);
    // console.log('[MIS WSS] prop memberCredentials:', memberCredentials);
    // console.log('[MIS WSS] local auth available:', hasValidCredentials(LOCAL_AUTH));
    // console.log('[MIS WSS] effectiveCredentials:', effectiveCredentials);

    if (!hasValidCredentials(effectiveCredentials)) {
      console.warn('[MIS WSS] Missing credentials. Client will not connect.', {
        authMode: AUTH_MODE,
        memberCredentials,
        localAuth: LOCAL_AUTH,
      });

      useMarketIntelligenceStore
        .getState()
        .setError('Missing MIS WebSocket credentials.');

      return undefined;
    }

    stopRequestedRef.current = false;

    function clearReconnectTimer() {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    }

    function clearPingTimer() {
      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    }

    function closeSocket() {
      if (!socketRef.current) return;

      try {
        socketRef.current.onopen = null;
        socketRef.current.onmessage = null;
        socketRef.current.onerror = null;
        socketRef.current.onclose = null;

        if (
          socketRef.current.readyState === WebSocket.OPEN ||
          socketRef.current.readyState === WebSocket.CONNECTING
        ) {
          socketRef.current.close();
        }
      } catch (error) {
        console.warn('[MIS WSS] Error closing socket:', error);
      }

      socketRef.current = null;
    }

    function sendPing() {
      const socket = socketRef.current;

      if (!socket || socket.readyState !== WebSocket.OPEN) return;

      try {
        socket.send(
          JSON.stringify({
            type: 'ping',
            ts: Date.now(),
          })
        );
      } catch (error) {
        console.warn('[MIS WSS] Failed to send ping:', error);
      }
    }

    function startPingTimer() {
      clearPingTimer();

      pingTimerRef.current = setInterval(() => {
        sendPing();
      }, CLIENT_PING_INTERVAL_MS);
    }

    function handleMessage(message) {
      const store = useMarketIntelligenceStore.getState();
      const type = getMessageType(message);
      const payload = getMessagePayload(message);

      // console.log('[MIS WSS] message received:', message);
      // console.log('[MIS WSS] message type:', type);
      // console.log('[MIS WSS] message payload:', payload);

      switch (type) {
        case 'auth_success':
          console.log('[MIS WSS] auth_success');
          store.setAuthSuccess();
          return;

        case 'auth_error':
          console.error('[MIS WSS] auth_error:', message);

          store.setAuthError(
            payload?.reason ||
              payload?.message ||
              message?.reason ||
              message?.message ||
              'MIS authentication failed'
          );
          return;

        case 'market_intelligence_initial_snapshot':
          // console.log(
          //   '[MIS WSS] initial snapshot visible posts:',
          //   payload?.visible_frontend_posts || payload?.visibleFrontendPosts || []
          // );

          store.setSnapshot(payload, {
            updateSource: 'initial_snapshot',
          });
          return;

        case 'market_intelligence_update':
          // console.log(
          //   '[MIS WSS] live update visible posts:',
          //   payload?.visible_frontend_posts || payload?.visibleFrontendPosts || []
          // );

          store.setSnapshot(payload, {
            updateSource: payload?.update_source || 'live_update',
            updateReason: payload?.update_reason || null,
          });
          return;

        case 'market_intelligence_snapshot_response':
          // console.log(
          //   '[MIS WSS] snapshot response visible posts:',
          //   payload?.visible_frontend_posts || payload?.visibleFrontendPosts || []
          // );

          store.setSnapshot(payload, {
            updateSource: 'snapshot_response',
          });
          return;

        case 'heartbeat':
          store.setHeartbeat(message);
          return;

        case 'ping':
          if (
            socketRef.current &&
            socketRef.current.readyState === WebSocket.OPEN
          ) {
            socketRef.current.send(
              JSON.stringify({
                type: 'pong',
                ts: Date.now(),
              })
            );
          }
          return;

        case 'pong':
          store.setHeartbeat(message);
          return;

        case 'server_error':
          console.error('[MIS WSS] server_error:', message);

          store.setError(
            payload?.message ||
              payload?.reason ||
              message?.message ||
              'MIS server error'
          );
          return;

        default:
          console.log('[MIS WSS] Unhandled message:', message);
      }
    }

    function scheduleReconnect() {
      if (stopRequestedRef.current) return;

      clearReconnectTimer();

      // console.log(`[MIS WSS] scheduling reconnect in ${RECONNECT_DELAY_MS}ms`);

      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, RECONNECT_DELAY_MS);
    }

    function connect() {
      const store = useMarketIntelligenceStore.getState();

      clearReconnectTimer();
      clearPingTimer();
      closeSocket();

      store.setConnectionStatus('connecting');


      const socket = new WebSocket(WS_URL);
      socketRef.current = socket;

      socket.onopen = () => {
        // console.log('[MIS WSS] connected. Sending auth...');

        store.setConnectionStatus('connected');

        const authPayload = buildAuthPayload(effectiveCredentials);


        socket.send(JSON.stringify(authPayload));

        startPingTimer();
      };

      socket.onmessage = (event) => {
        const message = normalizeMessage(event.data);
        if (!message) return;

        handleMessage(message);
      };

      socket.onerror = (error) => {
        console.error('[MIS WSS] socket error:', error);

        useMarketIntelligenceStore
          .getState()
          .setError('MIS WebSocket error.');
      };

      socket.onclose = (event) => {
        console.warn('[MIS WSS] closed:', {
          code: event.code,
          reason: event.reason || 'none',
          wasClean: event.wasClean,
        });

        clearPingTimer();

        useMarketIntelligenceStore
          .getState()
          .setConnectionStatus('disconnected');

        if (!stopRequestedRef.current) {
          scheduleReconnect();
        }
      };
    }

    connect();

    return () => {
      console.log('[MIS WSS] cleanup triggered');

      stopRequestedRef.current = true;

      clearReconnectTimer();
      clearPingTimer();
      closeSocket();

      useMarketIntelligenceStore.getState().setConnectionStatus('closed');
    };
  }, [
    effectiveCredentials?.memberId,
    effectiveCredentials?.planId,
    effectiveCredentials?.status,
  ]);

  return null;
}

export default MarketIntelligenceWebsocketClient;